"use client";

import { useMemo } from "react";
import useFetch from "@/hooks/useFetch";
import { getWarehouseList, WarehouseItem } from "@/requests/warehouse";

interface WarehouseStockFormProps {
  warehouseStocks: { warehouseId: number; qty: number }[];
  values: Record<string, string>;
  onChange: (warehouseId: number, value: string) => void;
}

const WarehouseStockForm = ({
  warehouseStocks,
  values,
  onChange,
}: WarehouseStockFormProps) => {
  const res = useFetch(() => getWarehouseList({ page: 1, limit: 1000 }), {});

  const warehouseItems: WarehouseItem[] = res.response?.Data || [];

  // Build the list from all warehouses and use zero for warehouses without stock.
  const stockEntries = useMemo(() => {
    const stockByWarehouse = new Map(
      warehouseStocks.map((stock) => [stock.warehouseId, stock.qty]),
    );

    return warehouseItems.map((warehouse) => ({
      warehouseId: warehouse.id,
      warehouseName: warehouse.name,
      currentQty: stockByWarehouse.get(warehouse.id) ?? 0,
    }));
  }, [warehouseItems, warehouseStocks]);

  if (res.loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
        <span className="ml-2 text-sm text-gray-500">Loading warehouses...</span>
      </div>
    );
  }

  if (warehouseItems.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-gray-400">
        No warehouses available
      </div>
    );
  }

  return (
    <div className="w-full">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left py-2 px-3 font-medium">Warehouse</th>
            <th className="text-center py-2 px-3 font-medium">Quantity</th>
          </tr>
        </thead>
        <tbody>
          {stockEntries.map((entry) => (
            <tr key={entry.warehouseId} className="border-b border-gray-100">
              <td className="py-2 px-3 text-gray-700">{entry.warehouseName}</td>
              <td className="py-2 px-3">
                <input
                  type="number"
                  min={0}
                  value={values[entry.warehouseId] ?? entry.currentQty}
                  onChange={(e) => onChange(entry.warehouseId, e.target.value)}
                  className="w-full max-w-[120px] mx-auto block text-center border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {stockEntries.length > 0 && (
        <p className="text-xs text-gray-400 mt-2 text-center">
          Total:{" "}
          {stockEntries.reduce(
            (sum, entry) =>
              sum + (parseInt(values[entry.warehouseId] ?? String(entry.currentQty)) || 0),
            0,
          )}
        </p>
      )}
    </div>
  );
};

export default WarehouseStockForm;
